import React from "react";

import { getDependents, getEmployeeForm } from "EmployeeApplication/api";
import { useSelector } from "react-redux";
import formatCurrency from "Utils/formatCurrency";
import { formatNameFL } from "Utils/formatName";

import BodyText from "Cobalt/BodyText";
import { CardSection } from "Cobalt/Card";
import { Table, TD, TR } from "Cobalt/TableV2";
import CarrierLogo from "Common/CarrierLogo";
import { Li, Ul } from "Common/HTML";
import Icon from "Common/Icon";
import InfoPopover from "Common/InfoPopover";

import { LongTermDisabilityEnrollOrDeclineButton } from "./longTermDisability.modals";

import type { LongTermDisabilityPlan, PlanCost } from "Utils/types";

const AgeCostRows = ({ plansData }) => {
  const dependents = useSelector(getDependents);
  const employeeForm = useSelector(getEmployeeForm);

  // Assuming all plans have the same pricing structure.
  const personIDsInQuote = Object.keys(plansData[0].planCost.pricing);
  const dependentsInQuote = dependents.filter(({ id }) => personIDsInQuote.includes(id));
  if (dependentsInQuote.length === 0) {
    return (
      <TR highlightFirstCell={false} withBorder={false}>
        <TD>{formatNameFL(employeeForm)}</TD>
        {plansData.map(({ plan, planCost }) => {
          const breakdown = planCost.costBreakdowns.age;
          return (
            <TD key={plan.id} align="left">
              {formatCurrency(breakdown.employeeTotalCost)}
            </TD>
          );
        })}
      </TR>
    );
  }
  return (
    <>
      <TR highlightFirstCell={false} withBorder={false}>
        <TD>{formatNameFL(employeeForm)}</TD>
        {plansData.map(({ plan, planCost }) => {
          const breakdown = planCost.costBreakdowns.age;
          return (
            <TD key={plan.id} align="left">
              {formatCurrency(breakdown.employeeCost)}
            </TD>
          );
        })}
      </TR>
      <TR highlightFirstCell={false} withBorder={false}>
        <TD>Dependents</TD>
        {plansData.map(({ plan, planCost }) => {
          const breakdown = planCost.costBreakdowns.age;
          return (
            <TD key={plan.id} align="left">
              {formatCurrency(breakdown.dependentCost)}
            </TD>
          );
        })}
      </TR>
      <TR highlightFirstCell={false} withBorder={false}>
        <TD>Total Monthly Cost</TD>
        {plansData.map(({ plan, planCost }) => {
          const breakdown = planCost.costBreakdowns.age;
          return (
            <TD key={plan.id} align="left">
              {formatCurrency(breakdown.employeeTotalCost)}
            </TD>
          );
        })}
      </TR>
    </>
  );
};

const SEGMENT_LABELS = {
  eeOnly: "Just Me",
  eeSpouse: "Me and My Spouse",
  eeChildren: "Me and My Children",
  eeFamily: "Me, My Spouse, and My Children",
};

const SegmentCostRows = ({ plansData }) => {
  // Assuming all plans have the same pricing structure.
  const breakdowns = plansData[0].planCost.costBreakdowns;
  return (
    <>
      {["eeOnly", "eeSpouse", "eeChildren", "eeFamily"].map((segment) => {
        if (breakdowns[segment] === undefined) {
          return null;
        }
        return (
          <TR key={segment} highlightFirstCell={false} withBorder={false}>
            <TD>{SEGMENT_LABELS[segment]}</TD>
            {plansData.map(({ plan }) => (
              <TD key={plan.id} align="left">
                {formatCurrency(breakdowns[segment].employeeTotalCost)}
              </TD>
            ))}
          </TR>
        );
      })}
    </>
  );
};

interface Props {
  plans: LongTermDisabilityPlan[];
  planCosts: PlanCost[];
}

export const LongTermDisabilityMultiplePlans = ({ plans, planCosts }: Props) => {
  // Pair up objects, just for sanity's sake.
  const plansData = plans.map((plan) => ({
    plan,
    planCost: planCosts.find((planCost) => planCost.planID === plan.id),
  }));

  return (
    <CardSection className="overflow-scroll">
      <Table>
        <TR withBorder={false}>
          <TD width="150px">&nbsp;</TD>
          {plansData.map(({ plan, planCost }) => (
            <TD key={plan.id} align="left">
              <CarrierLogo plan={plan} maxSize={3} />
              <BodyText mb={4}>{plan.display_name}</BodyText>
            </TD>
          ))}
        </TR>
        <TR bgColor="gray" withBorder={false}>
          <TD>Premium Overview</TD>
          {plansData.map(({ plan }) => (
            <TD key={plan.id} align="left">
              Your Cost
              <InfoPopover
                infoTitle="Payroll Contributions"
                infoText="This is the amount you pay each month, taken directly out of your paycheck."
                placement="top"
              />
            </TD>
          ))}
        </TR>
        {planCosts[0].pricingType === "age" ? <AgeCostRows plansData={plansData} /> : <SegmentCostRows plansData={plansData} />}
        <TR withBorder={false}>
          <TD>&nbsp;</TD>
          {plansData.map(({ plan, planCost }) => (
            <TD key={plan.id} align="left">
              <LongTermDisabilityEnrollOrDeclineButton plan={plan} planCost={planCost} my={3} />
            </TD>
          ))}
        </TR>
        <TR bgColor="gray" withBorder={false}>
          <TD colSpan={99} align="left">
            Plan Overview
          </TD>
        </TR>
        <TR highlightFirstCell={false} withBorder={false}>
          <TD>Plan Type</TD>
          {plansData.map(({ plan }) => (
            <TD align="left">Long Term Disability Insurance</TD>
          ))}
        </TR>
        <TR highlightFirstCell={false} withBorder={false}>
          <TD>Coverage Amount</TD>
          {plansData.map(({ plan }) => (
            <TD align="left">{formatCurrency(plan.coverage_amount)}</TD>
          ))}
        </TR>
        <TR highlightFirstCell={false} withBorder={false}>
          <TD>Maximum Duration of Benefits</TD>
          <TD align="left">{plan.benefit_duration}</TD>
        </TR>
        <TR highlightFirstCell={false} withBorder={false}>
          <TD>Elimination Period</TD>
          <TD align="left">{plan.elimination_period}</TD>
        </TR>
        <TR highlightFirstCell={false} withBorder={false}>
          <TD verticalAlign="top" py={4}>
            Other Benefits
          </TD>
          {plansData.map(({ plan }) => (
            <TD align="left" pt={4}>
              <Ul pl={4}>
                {plan.benefits.map((benefit, index) => (
                  <Li key={index}>{benefit}</Li>
                ))}
              </Ul>
            </TD>
          ))}
        </TR>
        <TR withBorder={false}>
          <TD>&nbsp;</TD>
          {plansData.map(({ plan }) => (
            <TD align="left">
              {plan.has_sbc && (
                <a href={plan.sbc_url} target="_blank" rel="noreferrer">
                  <Icon flavor="solid" icon="download" mr={3} />
                  Summary of Benefits and Coverage
                </a>
              )}
            </TD>
          ))}
        </TR>
      </Table>
    </CardSection>
  );
};
